import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../services/apiClient';
import { useContent } from '../../contexts/ContentContext';
import './BackupsRestoreAdmin.css';

const CONFIRM_WORD = 'RESTAURER';
const LATEST_BACKUP_KEY = 'content:backup:latest';

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0 o';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatMediaStatus(media) {
  if (!media) return 'Médias : en attente…';
  if (media.status === 'unavailable') return 'Médias : stockage R2 indisponible';
  if (media.complete) {
    return `Médias : ${media.copiedObjects || 0} fichier(s), ${formatBytes(media.copiedBytes || 0)} ✓`;
  }
  if (media.status === 'partial') {
    return `Médias : ${media.copiedObjects || 0} fichier(s) (copie partielle)`;
  }
  if (media.status === 'failed') return 'Médias : échec de la copie — réessayez';
  if (media.status === 'in_progress') {
    return `Médias : copie en cours… ${media.copiedObjects || 0} fichier(s)`;
  }
  return 'Médias : en attente…';
}

const BackupsRestoreAdmin = () => {
  const { loadContent, cancelPendingWrites, content } = useContent();
  const [manualBackups, setManualBackups] = useState([]);
  const [autoBackups, setAutoBackups] = useState([]);
  const [latestBackup, setLatestBackup] = useState(null);
  const [settings, setSettings] = useState({ frequency: 'weekly', lastAutoAt: null });
  const [maxManual, setMaxManual] = useState(10);
  const [maxAuto, setMaxAuto] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [selectedManualKey, setSelectedManualKey] = useState('');
  const [selectedAutoKey, setSelectedAutoKey] = useState('');
  const [restoreKey, setRestoreKey] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [frequencyDraft, setFrequencyDraft] = useState('weekly');
  const [mediaProgress, setMediaProgress] = useState('');
  const mountedRef = useRef(true);
  const messageTimerRef = useRef(null);
  const mediaPollRef = useRef(null);

  useEffect(() => () => {
    mountedRef.current = false;
    if (mediaPollRef.current) clearInterval(mediaPollRef.current);
  }, []);

  const showMessage = (type, text) => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setMessage({ type, text });
    messageTimerRef.current = setTimeout(() => setMessage({ type: '', text: '' }), 8000);
  };

  const findBackupMedia = (cmsBackupKey, manual, auto) => {
    const entry = [...manual, ...auto].find((b) => b.backupKey === cmsBackupKey);
    return entry?.media || null;
  };

  const pollUntilMediaComplete = async (cmsBackupKey, { maxAttempts = 120 } = {}) => {
    if (!cmsBackupKey) return true;
    for (let i = 0; i < maxAttempts; i += 1) {
      const response = await apiClient.listContentBackups();
      if (!response.success) throw new Error(response.error || 'Impossible de suivre la copie médias');
      const manual = response.manualBackups || [];
      const auto = response.autoBackups || [];
      const media = findBackupMedia(cmsBackupKey, manual, auto);
      if (mountedRef.current) {
        setManualBackups(manual);
        setAutoBackups(auto);
        setMediaProgress(formatMediaStatus(media));
      }
      if (media?.complete) {
        if (mountedRef.current) setMediaProgress('');
        return true;
      }
      if (media?.status === 'failed') {
        throw new Error('La copie des médias a échoué côté serveur.');
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    if (mountedRef.current) {
      setMediaProgress('Copie médias toujours en cours côté serveur — actualisez dans quelques minutes.');
    }
    return false;
  };

  const loadBackups = useCallback(async ({ silentPruneMessage = false, soft = false } = {}) => {
    try {
      if (!soft) setLoading(true);
      setError('');
      const response = await apiClient.listContentBackups();
      if (response.success) {
        const manual = response.manualBackups || [];
        const auto = response.autoBackups || [];
        setManualBackups(manual);
        setAutoBackups(auto);
        setLatestBackup(response.latestBackup || null);
        setSettings(response.settings || { frequency: 'weekly', lastAutoAt: null });
        setFrequencyDraft(response.settings?.frequency === 'monthly' ? 'monthly' : 'weekly');
        setMaxManual(response.maxManualBackups || 10);
        setMaxAuto(response.maxAutoBackups || 10);

        if (!silentPruneMessage && response.pruned > 0) {
          showMessage(
            'success',
            `${response.pruned} ancienne(s) sauvegarde(s) nettoyée(s).`
          );
        }

        setSelectedManualKey((prev) => {
          if (manual.some((b) => b.backupKey === prev)) return prev;
          return manual[0]?.backupKey || '';
        });

        setSelectedAutoKey((prev) => {
          if (auto.some((b) => b.backupKey === prev)) return prev;
          return auto[0]?.backupKey || '';
        });

        const latest = response.latestBackup || null;
        const restoreOptions = [];
        if (latest) restoreOptions.push(latest);
        restoreOptions.push(...manual, ...auto);

        setRestoreKey((prev) => {
          if (restoreOptions.some((b) => b.backupKey === prev)) return prev;
          return restoreOptions[0]?.backupKey || '';
        });

        return { manual, auto };
      }
      return { manual: [], auto: [] };
    } catch (err) {
      console.error('Error loading backups:', err);
      setError('Impossible de charger les sauvegardes. Vérifiez votre connexion admin.');
      return { manual: [], auto: [] };
    } finally {
      if (!soft) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  useEffect(() => {
    const pending = [...manualBackups, ...autoBackups].filter(
      (b) => b.backupKey && b.media && !b.media.complete && b.media.status !== 'failed'
    );
    if (!pending.length || mediaPollRef.current) return;
    const targetKey = pending[0].backupKey;
    mediaPollRef.current = setInterval(async () => {
      const { manual, auto } = await loadBackups({ silentPruneMessage: true, soft: true });
      const media = findBackupMedia(targetKey, manual, auto);
      if (media?.complete || media?.status === 'failed') {
        clearInterval(mediaPollRef.current);
        mediaPollRef.current = null;
        if (mountedRef.current) setMediaProgress('');
      } else if (mountedRef.current) {
        setMediaProgress(formatMediaStatus(media));
      }
    }, 3000);
    return () => {
      if (mediaPollRef.current) {
        clearInterval(mediaPollRef.current);
        mediaPollRef.current = null;
      }
    };
  }, [manualBackups, autoBackups, loadBackups]);

  const buildRestoreOptions = (manual, auto, latest) => {
    const options = [];
    if (latest) options.push(latest);
    options.push(...manual, ...auto);
    return options;
  };

  const restoreOptions = buildRestoreOptions(manualBackups, autoBackups, latestBackup);

  const handleCreateManualBackup = async () => {
    try {
      setBusy(true);
      const response = await apiClient.createContentBackup(content);
      if (!response.success) {
        showMessage('error', response.error || 'Erreur lors de la création de la sauvegarde.');
        return;
      }
      const newKey = response.backupKey;
      if (response.manualBackups?.length) {
        setManualBackups(response.manualBackups);
      }
      if (newKey) {
        setSelectedManualKey(newKey);
        setRestoreKey(newKey);
      }
      setMediaProgress('Médias : copie en cours côté serveur…');
      const mediaDone = await pollUntilMediaComplete(newKey);
      showMessage(
        'success',
        mediaDone
          ? 'Sauvegarde complète créée (contenu + médias R2).'
          : 'Sauvegarde contenu créée — copie médias en cours (serveur).'
      );
      await loadBackups({ silentPruneMessage: true, soft: true });
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la création de la sauvegarde.');
      setMediaProgress('');
      await loadBackups({ silentPruneMessage: true, soft: true });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveFrequency = async () => {
    try {
      setBusy(true);
      const response = await apiClient.updateBackupSettings(frequencyDraft);
      if (response.success) {
        setSettings(response.settings);
        if (response.autoBackups?.length) {
          setAutoBackups(response.autoBackups);
          const newKey = response.autoBackups[0]?.backupKey;
          if (newKey) setSelectedAutoKey(newKey);
        }
        const freqLabel = frequencyDraft === 'monthly' ? 'mensuelle' : 'hebdomadaire';
        if (response.autoBackupCreated) {
          showMessage(
            'success',
            `Fréquence ${freqLabel} enregistrée — première sauvegarde automatique créée.`
          );
        } else {
          showMessage(
            'success',
            `Fréquence ${freqLabel} enregistrée. La prochaine sauvegarde auto se fera automatiquement à l'échéance.`
          );
        }
        if (response.autoBackupCreated && response.autoBackups?.[0]?.backupKey) {
          setMediaProgress('Médias : copie en cours côté serveur…');
          await pollUntilMediaComplete(response.autoBackups[0].backupKey, { maxAttempts: 60 });
        }
        await loadBackups({ silentPruneMessage: true, soft: true });
      }
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la mise à jour des paramètres.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (backupKey, kind) => {
    if (!backupKey || backupKey === LATEST_BACKUP_KEY) {
      showMessage('error', 'Sélectionnez une sauvegarde valide à supprimer.');
      return;
    }
    if (
      !window.confirm(
        'Supprimer définitivement cette sauvegarde ? Cette action est irréversible.'
      )
    ) {
      return;
    }

    try {
      setBusy(true);
      const response = await apiClient.deleteContentBackup(backupKey);
      if (response.success) {
        showMessage('success', 'Sauvegarde supprimée.');
        if (kind === 'manual') setSelectedManualKey('');
        if (kind === 'auto') setSelectedAutoKey('');
        await loadBackups();
      }
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la suppression.');
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreKey) {
      showMessage('error', 'Sélectionnez une sauvegarde à restaurer.');
      return;
    }
    if (confirmText !== CONFIRM_WORD) {
      showMessage('error', `Tapez ${CONFIRM_WORD} pour confirmer la restauration.`);
      return;
    }
    if (!window.confirm('Cette action remplace tout le contenu actuel par la sauvegarde sélectionnée. Continuer ?')) {
      return;
    }

    try {
      setBusy(true);
      cancelPendingWrites();
      const response = await apiClient.restoreContentBackup(restoreKey);
      if (response.success) {
        const mediaNote = response.media_restore_queued
          ? ' Restauration des médias R2 lancée côté serveur.'
          : '';
        showMessage(
          'success',
          `Contenu restauré (${response.sections_updated || 0} sections).${mediaNote}`
        );
        setConfirmText('');
        await loadContent();
        await loadBackups();
      }
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la restauration.');
    } finally {
      setBusy(false);
    }
  };

  const formatLastAuto = (iso) => {
    if (!iso) return 'Aucune sauvegarde automatique encore';
    try {
      return new Date(iso).toLocaleString('fr-FR');
    } catch {
      return iso;
    }
  };

  const selectedManual = manualBackups.find((b) => b.backupKey === selectedManualKey);
  const selectedAuto = autoBackups.find((b) => b.backupKey === selectedAutoKey);

  return (
    <div className="backups-restore-admin">
      <div className="settings-header">
        <h3>
          <i className="fas fa-history"></i>
          Sauvegardes &amp; Restauration
        </h3>
        <p>
          Chaque sauvegarde <strong>manuelle</strong> ou <strong>automatique</strong> inclut le{' '}
          <strong>contenu du site</strong> (textes, pages, URLs) <strong>et les fichiers médias R2</strong>{' '}
          (images, vidéos, PDF). Maximum <strong>{maxManual}</strong> versions manuelles et{' '}
          <strong>{maxAuto}</strong> automatiques.
        </p>
      </div>

      {message.text && (
        <div className={`backup-message ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'}`}></i>
          {message.text}
        </div>
      )}

      <div className="backup-actions-row">
        <button
          type="button"
          className="btn-secondary"
          onClick={loadBackups}
          disabled={busy || loading}
        >
          <i className="fas fa-sync-alt"></i>
          Actualiser la liste
        </button>
      </div>

      {mediaProgress && (
        <p className="backup-hint media-progress">
          <i className="fas fa-spinner fa-spin"></i> {mediaProgress}
        </p>
      )}

      {loading ? (
        <div className="backup-loading">
          <i className="fas fa-spinner fa-spin"></i>
          Chargement des sauvegardes…
        </div>
      ) : error ? (
        <div className="backup-error">{error}</div>
      ) : (
        <div className="backup-panels">
          <section className="backup-panel backup-panel-auto">
            <h4>
              <i className="fas fa-clock"></i>
              Sauvegardes automatiques
            </h4>
            <p className="backup-panel-desc">
              Créées <strong>automatiquement</strong> selon la fréquence (contrôle quotidien serveur).
              Chaque version inclut <strong>contenu + médias R2</strong>. Maximum{' '}
              <strong>{maxAuto}</strong> versions.
            </p>

            <div className="backup-frequency-row">
              <label htmlFor="auto-frequency">Fréquence</label>
              <select
                id="auto-frequency"
                value={frequencyDraft}
                onChange={(e) => setFrequencyDraft(e.target.value)}
                disabled={busy}
              >
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuelle</option>
              </select>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveFrequency}
                disabled={busy || frequencyDraft === settings.frequency}
              >
                Enregistrer la fréquence
              </button>
            </div>
            <p className="backup-hint">
              Dernière auto : {formatLastAuto(settings.lastAutoAt)}
            </p>

            {autoBackups.length === 0 ? (
              <div className="backup-empty">Aucune sauvegarde automatique pour le moment.</div>
            ) : (
              <div className="backup-list-section">
                <label htmlFor="auto-backup-select">Version automatique</label>
                <select
                  id="auto-backup-select"
                  value={selectedAutoKey}
                  onChange={(e) => setSelectedAutoKey(e.target.value)}
                  disabled={busy}
                >
                  {autoBackups.map((backup) => (
                    <option key={backup.backupKey} value={backup.backupKey}>
                      {backup.label}
                    </option>
                  ))}
                </select>
                {selectedAuto && (
                  <>
                    <p className="backup-hint">
                      Clé : <code>{selectedAuto.backupKey}</code>
                    </p>
                    <p className="backup-hint">{formatMediaStatus(selectedAuto.media)}</p>
                  </>
                )}
                <div className="backup-item-actions">
                  <button
                    type="button"
                    className="btn-danger-outline"
                    onClick={() => handleDelete(selectedAutoKey, 'auto')}
                    disabled={busy || !selectedAutoKey}
                  >
                    <i className="fas fa-trash-alt"></i>
                    Supprimer
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className="backup-panel backup-panel-manual">
            <h4>
              <i className="fas fa-hand-paper"></i>
              Sauvegardes manuelles
            </h4>
            <p className="backup-panel-desc">
              Créez une version avant une grosse édition. Inclut{' '}
              <strong>contenu + médias R2</strong>. Maximum <strong>{maxManual}</strong> versions.
            </p>

            <button
              type="button"
              className="btn-primary"
              onClick={handleCreateManualBackup}
              disabled={busy}
            >
              <i className="fas fa-save"></i>
              Créer une sauvegarde manuelle
            </button>

            {manualBackups.length === 0 ? (
              <div className="backup-empty">Aucune sauvegarde manuelle pour le moment.</div>
            ) : (
              <div className="backup-list-section">
                <label htmlFor="manual-backup-select">Version manuelle</label>
                <select
                  id="manual-backup-select"
                  value={selectedManualKey}
                  onChange={(e) => setSelectedManualKey(e.target.value)}
                  disabled={busy}
                >
                  {manualBackups.map((backup) => (
                    <option key={backup.backupKey} value={backup.backupKey}>
                      {backup.label}
                    </option>
                  ))}
                </select>
                {selectedManual && (
                  <>
                    <p className="backup-hint">
                      Clé : <code>{selectedManual.backupKey}</code>
                    </p>
                    <p className="backup-hint">{formatMediaStatus(selectedManual.media)}</p>
                  </>
                )}
                <div className="backup-item-actions">
                  <button
                    type="button"
                    className="btn-danger-outline"
                    onClick={() => handleDelete(selectedManualKey, 'manual')}
                    disabled={busy || !selectedManualKey}
                  >
                    <i className="fas fa-trash-alt"></i>
                    Supprimer
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      <div className="restore-confirm-section">
        <h4>Restaurer le contenu</h4>
        <p className="restore-warning">
          <i className="fas fa-exclamation-triangle"></i>
          La restauration remplace <strong>tout</strong> le contenu du site et restaure les{' '}
          <strong>médias R2 manquants</strong> liés à cette version. Une sauvegarde CMS de
          l&apos;état actuel est créée avant la restauration.
        </p>

        {restoreOptions.length > 0 && (
          <div className="backup-list-section">
            <label htmlFor="restore-select">Sauvegarde à restaurer</label>
            <select
              id="restore-select"
              value={restoreKey}
              onChange={(e) => setRestoreKey(e.target.value)}
              disabled={busy}
            >
              {latestBackup && (
                <option value={latestBackup.backupKey}>
                  ★ {latestBackup.label}
                </option>
              )}
              {manualBackups.length > 0 && (
                <optgroup label="Manuelles">
                  {manualBackups.map((backup) => (
                    <option key={backup.backupKey} value={backup.backupKey}>
                      {backup.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {autoBackups.length > 0 && (
                <optgroup label="Automatiques">
                  {autoBackups.map((backup) => (
                    <option key={backup.backupKey} value={backup.backupKey}>
                      {backup.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        )}

        <label htmlFor="restore-confirm">
          Tapez <strong>{CONFIRM_WORD}</strong> pour confirmer
        </label>
        <input
          id="restore-confirm"
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={CONFIRM_WORD}
          disabled={busy || !restoreKey}
          autoComplete="off"
        />
        <button
          type="button"
          className="btn-danger"
          onClick={handleRestore}
          disabled={busy || !restoreKey || confirmText !== CONFIRM_WORD}
        >
          {busy ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Restauration…
            </>
          ) : (
            <>
              <i className="fas fa-undo"></i>
              Restaurer cette sauvegarde
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BackupsRestoreAdmin;
